import { useCallback } from '@wordpress/element';
import { useDispatch } from '@wordpress/data';
import { store as blockEditorStore } from '@wordpress/block-editor';
import { store as noticesStore } from '@wordpress/notices';
import { parse } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import useProLicense from '../hooks/useProLicense';

async function uploadViaPHP(url) {
	try {
		const response = await fetch(`${window.wpApiSettings.root}vayu-blocks/v1/upload-media`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': window.wpApiSettings.nonce,
			},
			body: JSON.stringify({ url }),
		});
		if (!response.ok) throw new Error('Upload via PHP failed');
		const data = await response.json();
		return data.source_url;
	} catch (err) {
		console.error('[Upload error]', err);
		return null;
	}
}

function extractUrls(text) {
	return text.match(/https?:\/\/\S+\.(jpe?g|png|gif|webp|svg|mp4|mov|pdf)/gi) || [];
}

const useInsertPattern = (onInsertComplete) => {
	const { insertBlocks } = useDispatch(blockEditorStore);
	const { createNotice } = useDispatch(noticesStore);
	const { isProActive } = useProLicense();

	const insertPattern = useCallback(async (pattern, premium) => {
		// Early return for Pro check
		if (premium === 'pro' && !isProActive) {
			createNotice('warning', __('This template is available in the Pro version. Please upgrade to unlock.', 'vayu-blocks'), {
				type: 'snackbar',
				isDismissible: true,
			});
			return;
		}

		try {
			// Parse blocks immediately (no await if no URLs)
			const urls = extractUrls(pattern);
			let blocks;

			if (urls.length === 0) {
				// Fast path - no image processing needed
				blocks = parse(pattern);
			} else {
				// Process images only if they exist
				let updated = pattern;
				const uploadPromises = urls.map(async (url) => {
					const newUrl = await uploadViaPHP(url);
					if (newUrl) {
						updated = updated.replaceAll(url, newUrl);
					}
				});

				await Promise.all(uploadPromises);
				blocks = parse(updated);
			}

			if (!blocks?.length) {
				createNotice('error', __('No blocks found in template.', 'vayu-blocks'), {
					type: 'snackbar',
					isDismissible: true,
				});
				return;
			}

			// Get insertion position
			const blockEditor = wp.data.select('core/block-editor');
			const selected = blockEditor.getBlockSelectionStart();
			const insertIndex = selected !== null
				? blockEditor.getBlockIndex(selected) + 1
				: undefined;

			// Insert blocks
			insertBlocks(blocks, insertIndex);

			createNotice('success', __('Template inserted successfully!', 'vayu-blocks'), {
				type: 'snackbar',
				isDismissible: true,
			});

			onInsertComplete?.();

		} catch (err) {
			console.error('[Insert failed]', err);
			createNotice('error', __('Could not insert pattern. Please try again.', 'vayu-blocks'), {
				type: 'snackbar',
				isDismissible: true,
			});
		}
	}, [insertBlocks, onInsertComplete, createNotice, isProActive]);

	return insertPattern;
};

export default useInsertPattern;
